import React, { useState } from 'react'
import { router, usePage, Link, Head } from '@inertiajs/react'
import { LogIn, Loader2, AlertCircle, Eye, EyeOff, Mail, Check } from 'lucide-react'
import Marketing3AuthShell from '@/components/marketing3/Marketing3AuthShell'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { FALLBACK_BRAND, SUPPORT_EMAIL } from '@/lib/brand'
import '../marketing/marketing3.css'
import { csrfToken } from '@/lib/csrf'


// mkt3 teal focus ring — the marketing3.css ring var is operator-scoped, so inputs inside the
// mkt3-root shell get the landing's teal focus ring via this className override.
const FIELD_FOCUS = 'border-stone-200 focus-visible:ring-teal-600 focus-visible:ring-offset-0'

interface SignInProps {
  brand?: string | null
}

export default function SignIn({ brand: brandProp }: SignInProps) {
  const brand = typeof brandProp === 'string' && brandProp.trim() !== '' ? brandProp : FALLBACK_BRAND

  // Users::SessionsController inherits ApplicationController (not Operator::BaseController),
  // so a failed-login redirect's flash surfaces on Inertia's shared `flash` prop — read
  // here, display only. (Identical contract to the claim pages.)
  const { props } = usePage<{ flash?: { alert?: string | null; notice?: string | null } }>()
  const flashAlert = props.flash?.alert
  const flashNotice = props.flash?.notice

  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [remember, setRemember] = useState(false)
  const [show, setShow] = useState(false)
  const [submitting, setSubmitting] = useState(false)

  const canSubmit = email.trim().length > 0 && password.length > 0 && !submitting

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!canSubmit) return
    setSubmitting(true)
    // State-changing — router.post drives Devise's sessions#create (the documented
    // exception to the <Link> nav rule). Devise expects params nested under `user`.
    // The controller redirects to the portfolio on success, or back here with a
    // flash alert on bad credentials. CSRF token forwarded for parity with other
    // Inertia form posts (Inertia includes the form-encoded body automatically).
    router.post(
      '/users/sign_in',
      { user: { email, password, remember_me: remember ? '1' : '0' } },
      {
        headers: { 'X-CSRF-Token': csrfToken() },
        onFinish: () => setSubmitting(false),
      }
    )
  }

  return (
    <Marketing3AuthShell brand={brand}>
      <Head title="Sign in" />
      {/* Lead-in */}
      <div className="text-center">
        <h1 className="mkt3-display mkt3-h3">Sign in</h1>
        <p className="mkt3-prose mx-auto mt-2 text-sm">Welcome back. Sign in to manage your revenue.</p>
      </div>

      {/* One-time notices (e.g. after sign-out / password reset). */}
      {flashNotice && (
        <div className="mt-6 flex items-start gap-2 rounded-lg border border-teal-200 bg-teal-50 px-4 py-3 text-sm text-teal-800">
          <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
          <span>{flashNotice}</span>
        </div>
      )}

      {/* Invalid-credentials error — styled, never a raw 500/blank page. */}
      {flashAlert && (
        <div className="mt-6 flex items-start gap-2 rounded-lg border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-800">
          <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
          <span>{flashAlert}</span>
        </div>
      )}

      <form onSubmit={handleSubmit} className="mt-7 space-y-5">
        {/* Email */}
        <div className="space-y-2">
          <Label htmlFor="email" className="mkt3-label normal-case tracking-[0.12em] text-slate-500">
            Email
          </Label>
          <div className="relative">
            <Mail className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <Input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoComplete="email"
              autoFocus
              placeholder="you@company.com"
              className={`h-11 rounded-lg bg-white pl-9 ${FIELD_FOCUS}`}
            />
          </div>
        </div>

        {/* Password */}
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <Label htmlFor="password" className="mkt3-label normal-case tracking-[0.12em] text-slate-500">
              Password
            </Label>
            {/* :recoverable is enabled on User. /users/password/new is now a styled Inertia
                page (Users::PasswordsController → auth/ForgotPassword), so <Link> is correct. */}
            <Link href="/users/password/new" className="text-xs font-medium text-teal-700 hover:underline">
              Forgot password?
            </Link>
          </div>
          <div className="relative">
            <Input
              id="password"
              type={show ? 'text' : 'password'}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="current-password"
              placeholder="Enter your password"
              className={`h-11 rounded-lg bg-white pr-10 ${FIELD_FOCUS}`}
            />
            <button
              type="button"
              onClick={() => setShow((s) => !s)}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 transition-colors hover:text-slate-600"
              aria-label={show ? 'Hide password' : 'Show password'}
            >
              {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
          </div>
        </div>

        {/* Remember me — :rememberable is enabled on User. Same accessible styled
            checkbox idiom as the claim flow's ConsentRow (peer + overlaid Check). */}
        <label className="flex cursor-pointer items-center gap-2.5 text-sm text-slate-700">
          <span className="relative flex h-4 w-4 shrink-0">
            <input
              type="checkbox"
              checked={remember}
              onChange={(e) => setRemember(e.target.checked)}
              className="peer h-4 w-4 cursor-pointer appearance-none rounded border border-stone-300 bg-white transition-colors checked:border-teal-600 checked:bg-teal-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal-600 focus-visible:ring-offset-1"
            />
            <Check className="pointer-events-none absolute left-0 top-0 h-4 w-4 scale-0 text-white transition-transform peer-checked:scale-100" />
          </span>
          <span>Remember me on this device</span>
        </label>

        <Button
          type="submit"
          disabled={!canSubmit}
          className="mkt3-cta h-11 w-full rounded-lg bg-teal-600 text-sm font-semibold text-white hover:bg-teal-700"
        >
          {submitting ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Signing in…
            </>
          ) : (
            <>
              <LogIn className="mr-2 h-4 w-4" />
              Sign in
            </>
          )}
        </Button>
      </form>

      <p className="mkt3-label mt-7 text-center normal-case tracking-[0.12em] text-slate-500">
        Access is invitation-only. Need an account?{' '}
        <a href={`mailto:${SUPPORT_EMAIL}`} className="font-medium text-teal-700 hover:underline">
          Talk to our team
        </a>
      </p>
    </Marketing3AuthShell>
  )
}
